/**
 * Expert Canvas — UI/UX Animations
 * Handles: scroll reveal, hero Ken Burns, sticky header shrink,
 * product card hover, smooth anchor scroll, USP ticker
 */

(function () {
  'use strict';

  /* ── Scroll Reveal ── */
  function initScrollReveal() {
    const targets = document.querySelectorAll(
      '.multicolumn-card, .card-wrapper, .image-with-text, ' +
      '.rich-text, .collapsible-content__header, ' +
      '.title-wrapper, .email-signup-banner, .contact-form'
    );

    // Add reveal attributes based on position — this only tags legacy
    // Dawn component classes that don't already carry data-canvas-reveal.
    // It must NOT gate the observer setup below: the site's own custom
    // sections (Our Story, Best Sellers, Quality/Trust, Final CTA, etc.)
    // already hardcode data-canvas-reveal in their own templates and
    // never match these stock classes at all, so when a page has none of
    // them (true for this homepage since every stock-Dawn section was
    // replaced with a custom one), returning early here used to skip
    // observing every custom-tagged element too — leaving them stuck at
    // their CSS default (opacity: 0) forever, since nothing ever added
    // the .is-visible class that reveals them.
    targets.forEach(function (el, i) {
      if (el.hasAttribute('data-canvas-reveal')) return;
      const col = i % 4;
      if (col === 0)      el.setAttribute('data-canvas-reveal', 'left');
      else if (col === 3) el.setAttribute('data-canvas-reveal', 'right');
      else                el.setAttribute('data-canvas-reveal', '');
      el.classList.add('delay-' + (col + 1));
    });

    if (!document.querySelectorAll('[data-canvas-reveal]').length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('[data-canvas-reveal]').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Sticky Header Shrink ── */
  function initStickyHeader() {
    var header = document.querySelector('.header-wrapper');
    if (!header) return;

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrollY = window.scrollY || window.pageYOffset;
        header.classList.toggle('header-wrapper--scrolled', scrollY > 80);
        ticking = false;
      });
    }, { passive: true });
  }

  /* ── Hero Ken Burns slide activation ── */
  function initKenBurns() {
    var slides = document.querySelectorAll('.slideshow__slide');
    if (!slides.length) return;

    function activateSlide(slide) {
      slide.classList.add('is-selected');
    }

    function deactivateSlide(slide) {
      slide.classList.remove('is-selected');
    }

    // Activate visible slides
    var slideObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activateSlide(entry.target);
        } else {
          deactivateSlide(entry.target);
        }
      });
    }, { threshold: 0.5 });

    slides.forEach(function (slide) {
      slideObserver.observe(slide);
    });
  }

  /* ── Smooth Anchor Scroll ── */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ── Product card: prevent quick-add from blocking image click ── */
  function initProductCards() {
    document.querySelectorAll('.card-wrapper').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        this.classList.add('is-hovered');
      });
      card.addEventListener('mouseleave', function () {
        this.classList.remove('is-hovered');
      });
    });
  }

  /* ── Multicolumn step numbers highlight ── */
  function initProcessSteps() {
    var cols = document.querySelectorAll('.multicolumn-card__title');
    cols.forEach(function (title) {
      var text = title.textContent || '';
      var match = text.match(/^(0[1-4]\s*[—-]\s*)/);
      if (match) {
        title.innerHTML = title.innerHTML.replace(
          match[1],
          '<span style="color:var(--canvas-gold);font-size:0.85em;">' + match[1] + '</span>'
        );
      }
    });
  }

  /* ── Section gold dividers ── */
  function initSectionDividers() {
    var sections = document.querySelectorAll('.shopify-section');
    if (sections.length < 2) return;
    /* batch all insertions via a single requestAnimationFrame to avoid per-insert reflow */
    requestAnimationFrame(function () {
      sections.forEach(function (section, i) {
        if (i === 0) return;
        var hr = document.createElement('hr');
        hr.className = 'section-divider';
        section.parentNode.insertBefore(hr, section);
      });
    });
  }

  /* ── Re-observe reveal targets added after initial load ──
     Dawn's facet filtering (facets.js renderProductGridContainer) replaces
     #ProductGridContainer's innerHTML directly via a raw DOM swap — no
     event fires afterward, so any [data-canvas-reveal] card rendered by a
     filter/sort change would otherwise sit at opacity:0 forever, never
     picked up by the observer that only ran once at page load. */
  function observeNewRevealTargets(root) {
    root.querySelectorAll('[data-canvas-reveal]').forEach(function (el) {
      if (el.dataset.canvasRevealObserved) return;
      el.dataset.canvasRevealObserved = 'true';
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      io.observe(el);
    });
  }

  function initProductGridMutationWatcher() {
    var grid = document.getElementById('ProductGridContainer');
    if (!grid || !('MutationObserver' in window)) return;
    var mo = new MutationObserver(function () {
      observeNewRevealTargets(grid);
    });
    mo.observe(grid, { childList: true, subtree: true });
  }

  /* ── Init all on DOM ready ── */
  function init() {
    initScrollReveal();
    initStickyHeader();
    initKenBurns();
    initSmoothScroll();
    initProductCards();
    initProcessSteps();
    initSectionDividers();
    initProductGridMutationWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run after Shopify section lazy-loads */
  document.addEventListener('shopify:section:load', function () {
    initScrollReveal();
    initProductCards();
    initProcessSteps();
  });

})();
